import React, { ChangeEvent, useState } from "react";
import { ValueEditorProps } from "react-querybuilder";
import { TagsInputField } from "./fields/TagsInputField";
import { DjangoFieldType } from "./enums";
import { isDateTimeOperator, operatorRequiresValue, } from "./fields/utils";
import { JSONFieldEditor } from "./fields/jsonField";
import DatePickerField from "../datepicker/DatePickerField";
import DateTimePickerField from "../datepicker/DateTimePickerField";

// Define interfaces for field data and choices
interface FieldChoice {
    value: string;
    label: string;
}

interface FieldData {
    type?: string;
    label?: string;
    choices?: FieldChoice[];
    values?: any[];
    predicates?: string[];
}

// Extended ValueEditorProps to include fieldData
// @ts-ignore
interface ExtendedValueEditorProps extends ValueEditorProps {
    fieldData?: FieldData;
    context?: any;
}

export const ValueEditor: React.FC<ExtendedValueEditorProps> = (props: any) => {
    const { field, operator, value, handleOnChange, fieldData } = props;
    const [isValid, setIsValid] = useState<boolean>(true);

    // Get field type from fieldData
    const fieldType: DjangoFieldType = fieldData?.field_type || 'CharField';

    // Determine if this operator requires a value
    const requiresValue = operatorRequiresValue(operator);

    // Handle choices if available
    const hasChoices = fieldData?.choices && Array.isArray(fieldData.choices) && fieldData.choices.length > 0;


    // If operator doesn't require a value, set a default value and show a placeholder
    if (!requiresValue) {
        // Set the value to true for these operators
        if (value === undefined || value === null || value === '') {
            handleOnChange(true);
        }
        return <span style={{ color: 'var(--rd-text-tertiary)', flex: 1 }}>Значение не требуется</span>;
    }

    if (!hasChoices && !isDateTimeOperator(operator) && operator !== 'range') {
        if (fieldType === DjangoFieldType.DateTimeField) {
            return <DateTimePickerField name="" size="medium" floating clearable initialValue={typeof value === 'string' ? value : ''} onChange={handleOnChange} />;
        }
        if (fieldType === DjangoFieldType.DateField) {
            return <DatePickerField name="" size="medium" floating clearable initialValue={typeof value === 'string' ? value : ''} onChange={handleOnChange} />;
        }
    }

    // For range operators, render two inputs
    if (operator === 'range') {
        const startValue = Array.isArray(value) ? value[0] || '' : '';
        const endValue = Array.isArray(value) ? value[1] || '' : '';

        if (!hasChoices && (fieldType === DjangoFieldType.DateTimeField || fieldType === DjangoFieldType.DateField)) {
            const PickerField = fieldType === DjangoFieldType.DateTimeField ? DateTimePickerField : DatePickerField;
            const emitRange = (start: string, end: string) => {
                if (!start || !end) {
                    handleOnChange('');
                    return;
                }
                handleOnChange([start, end]);
            };
            return (
                <div style={{ display: 'flex', columnGap: '0.5rem', flex: 1 }}>
                    <PickerField
                        name=""
                        size="medium"
                        floating
                        clearable
                        initialValue={typeof startValue === 'string' ? startValue : ''}
                        onChange={(newValue) => emitRange(newValue, endValue)}
                    />
                    <PickerField
                        name=""
                        size="medium"
                        floating
                        clearable
                        initialValue={typeof endValue === 'string' ? endValue : ''}
                        onChange={(newValue) => emitRange(startValue, newValue)}
                    />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', columnGap: '0.5rem' }}>
                <TagsInputField
                    fieldType={fieldType}
                    operator="exact"
                    value={startValue}
                    fieldData={fieldData}
                    handleOnChange={(newValue) => {
                        handleOnChange([newValue, endValue]);
                    }}
                    isMany={false}
                    suggestions={fieldData?.choices}
                    allowNewTag={false}
                    fetchSuggestions={false}
                    suggestionsCache={props.context.suggestionsCache}
                />
                <TagsInputField
                    fieldType={fieldType}
                    operator="exact"
                    value={endValue}
                    fieldData={fieldData}
                    handleOnChange={(newValue) => {
                        handleOnChange([startValue, newValue]);
                    }}
                    isMany={false}
                    suggestions={fieldData?.choices}
                    allowNewTag={false}
                    fetchSuggestions={false}
                    suggestionsCache={props.context.suggestionsCache}
                />
            </div>
        );
    }

    // For fields with choices, render a select
    if (hasChoices) {
        return <TagsInputField
            fieldType={fieldType}
            operator={operator}
            value={value}
            fieldData={fieldData}
            handleOnChange={handleOnChange}
            isMany={["in", "contained_by", "overlap", "has_keys", "contains_all"].includes(operator)}
            suggestions={fieldData?.choices}
            allowNewTag={false}
            fetchSuggestions={false}
            suggestionsCache={props.context.suggestionsCache}
        />
    }

    // For boolean fields
    if (fieldType === 'BooleanField') {
        return (
            <div style={{ flex: 1 }}>
            <label className="rd-checkbox">
                <input
                    type="checkbox"
                    checked={value === true || value === 'true'}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const newValue = e.target.checked;
                        handleOnChange(newValue);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleOnChange(!(value === true || value === 'true'));
                        }
                    }}
                    className="rd-checkbox__input"
                    id={`boolean-${field}`}
                />
                <span className="rd-checkbox__box"></span>
                <span className="rd-checkbox__label">Да</span>
            </label>
            </div>
        );
    }

    // For JSONField with specific operators
    if (fieldType === 'JSONField') {
        return <JSONFieldEditor jsonValue={value} fieldType={fieldType} operator={operator} isValid={isValid}
            setIsValid={setIsValid} handleOnChange={handleOnChange} value={value} />
    }

    return <TagsInputField
        fieldType={fieldType}
        operator={operator}
        value={value}
        fieldData={fieldData}
        handleOnChange={handleOnChange}
        isMany={["in", "contained_by", "overlap", "has_keys", "contains_all"].includes(operator)}
        fetchSuggestions={![DjangoFieldType.DateField, DjangoFieldType.DateTimeField, DjangoFieldType.BigIntegerField, DjangoFieldType.IntegerField, DjangoFieldType.BigAutoField].includes(fieldType)}
        suggestionsCache={props.context.suggestionsCache}
    />
};
